import { HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { extractErrorMessage } from '@libs/shared/utils/functions/error-message.util';
import {
  ExpertEventNotificationRecord,
  ExpertEventNotificationService,
  ExpertEventNotificationType,
  ReadExpertEventNotificationRequest,
  SliceExpertEventNotificationRecord,
} from '@libs/shared/api';
import { PageQuery } from '@libs/shared/interface/page.interface';
import { tapResponse } from '@ngrx/operators';
import { patchState, signalState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { pipe, switchMap } from 'rxjs';
import { tap } from 'rxjs/operators';

export interface ExpertEventNotificationCoreQueryFilter {
  expertWorkspaceId?: string;
  active?: boolean;
  read?: boolean;
  types?: ExpertEventNotificationType[];
}

interface ExpertEventNotificationCoreState {
  slice: SliceExpertEventNotificationRecord;
  pageQuery: PageQuery<ExpertEventNotificationCoreQueryFilter>;
  isLoading: boolean;
  isLoaded: boolean;
  errorMessage: string;
  unreadCount: number;
}

const initialState: ExpertEventNotificationCoreState = {
  slice: undefined,
  pageQuery: {
    filter: {
      active: true,
    },
    page: 0,
    size: 25,
    sort: undefined,
  },
  isLoading: false,
  isLoaded: false,
  errorMessage: '',
  unreadCount: 0,
};

@Injectable({
  providedIn: 'root',
})
export class ExpertEventNotificationCoreStore {
  readonly #expertEventManagerNotificationService = inject(
    ExpertEventNotificationService
  );

  readonly state = signalState(initialState);
  readonly slice$$ = this.state.slice;
  readonly pageQuery$$ = this.state.pageQuery;
  readonly isLoading$$ = this.state.isLoading;
  readonly isLoaded$$ = this.state.isLoaded;
  readonly errorMessage$$ = this.state.errorMessage;
  readonly unreadCount$$ = this.state.unreadCount;

  constructor() {}

  updateExpertEventNotificationPageQuery(
    pageQuery: ExpertEventNotificationCoreQueryFilter
  ) {
    patchState(this.state, {
      pageQuery: {
        ...this.state().pageQuery,
        filter: pageQuery,
      },
    });
    this.fetchExpertEventNotificationSlice(pageQuery);
  }

  fetchExpertEventNotificationSlice =
    rxMethod<ExpertEventNotificationCoreQueryFilter>(
      pipe(
        tap(payload => {
          patchState(this.state, {
            ...this.state(),
            pageQuery: {
              ...this.state().pageQuery,
              page: 0,
              filter: payload,
            },
            isLoading: true,
          });
        }),
        switchMap(() =>
          this.#expertEventManagerNotificationService
            .fetchExpertEventNotificationSlice(
              this.pageQuery$$().filter.expertWorkspaceId,
              this.pageQuery$$().filter.active,
              this.pageQuery$$().filter.read,
              this.pageQuery$$().filter.types,
              this.pageQuery$$().page,
              this.pageQuery$$().size,
              [this.pageQuery$$().sort?.toString()]
            )
            .pipe(
              tapResponse(
                (slice: SliceExpertEventNotificationRecord) => {
                  patchState(this.state, {
                    slice,
                    isLoading: false,
                    isLoaded: true,
                    errorMessage: '',
                  });
                },
                (e: HttpErrorResponse) => {
                  patchState(this.state, {
                    slice: undefined,
                    isLoading: false,
                    isLoaded: false,
                    errorMessage: extractErrorMessage(e),
                  });
                }
              )
            )
        )
      )
    );

  fetchNextExpertEventNotificationSlice = rxMethod<void>(
    pipe(
      tap(() => {
        patchState(this.state, {
          ...this.state(),
          isLoading: true,
        });
      }),
      switchMap(() =>
        this.#expertEventManagerNotificationService
          .fetchExpertEventNotificationSlice(
            this.pageQuery$$().filter.expertWorkspaceId,
            this.pageQuery$$().filter.active,
            this.pageQuery$$().filter.read,
            this.pageQuery$$().filter.types,
            this.pageQuery$$().page + 1,
            this.pageQuery$$().size,
            [this.pageQuery$$().sort?.toString()]
          )
          .pipe(
            tapResponse(
              (slice: SliceExpertEventNotificationRecord) => {
                patchState(this.state, {
                  ...this.state(),
                  slice: {
                    ...slice,
                    content: [...this.slice$$().content, ...slice.content],
                  },
                  isLoading: false,
                  isLoaded: true,
                  errorMessage: '',
                });
              },
              (e: HttpErrorResponse) => {
                patchState(this.state, {
                  slice: undefined,
                  isLoading: false,
                  isLoaded: false,
                  errorMessage: extractErrorMessage(e),
                });
              }
            )
          )
      )
    )
  );

  fetchExpertEventNotificationById = rxMethod<{ id: string }>(
    pipe(
      switchMap(payload =>
        this.#expertEventManagerNotificationService
          .fetchExpertEventNotificationById(payload.id)
          .pipe(
            tapResponse(
              (record: ExpertEventNotificationRecord) => {
                patchState(this.state, {
                  slice: {
                    ...this.slice$$(),
                    content: this.slice$$().content.map(item =>
                      item.id === record.id ? record : item
                    ),
                  },
                  isLoading: false,
                  isLoaded: true,
                  errorMessage: '',
                });
              },
              (e: HttpErrorResponse) => {
                patchState(this.state, {
                  isLoading: false,
                  isLoaded: false,
                  errorMessage: extractErrorMessage(e),
                });
              }
            )
          )
      )
    )
  );

  fetchExpertEventNotificationUnreadCount =
    rxMethod<ExpertEventNotificationCoreQueryFilter>(
      pipe(
        tap(payload => {
          patchState(this.state, {
            ...this.state(),
            pageQuery: {
              filter: payload,
            },
          });
        }),
        switchMap(() =>
          this.#expertEventManagerNotificationService
            .fetchExpertEventNotificationUnreadCount(
              this.pageQuery$$().filter.expertWorkspaceId,
              this.pageQuery$$().filter.active,
              this.pageQuery$$().filter.read,
              this.pageQuery$$().filter.types
            )
            .pipe(
              tapResponse(
                (unreadCount: number) => {
                  patchState(this.state, {
                    unreadCount,
                  });
                },
                (e: HttpErrorResponse) => {
                  patchState(this.state, {
                    unreadCount: 0,
                  });
                }
              )
            )
        )
      )
    );

  readExpertEventNotification = rxMethod<{ id: string }>(
    pipe(
      switchMap(payload =>
        this.#expertEventManagerNotificationService
          .readExpertEventNotification(payload.id)
          .pipe(
            tapResponse(
              () => {
                patchState(this.state, {
                  slice: {
                    ...this.slice$$(),
                    content: this.slice$$().content.map(item =>
                      item.id === payload.id
                        ? { ...item, readAt: new Date().toISOString() }
                        : item
                    ),
                  },
                });
              },
              (e: HttpErrorResponse) => {
                patchState(this.state, {
                  errorMessage: extractErrorMessage(e),
                });
              }
            )
          )
      )
    )
  );

  unreadExpertEventNotification = rxMethod<{ id: string }>(
    pipe(
      switchMap(payload =>
        this.#expertEventManagerNotificationService
          .unreadExpertEventNotification(payload.id)
          .pipe(
            tapResponse(
              () => {
                patchState(this.state, {
                  slice: {
                    ...this.slice$$(),
                    content: this.slice$$().content.map(item =>
                      item.id === payload.id ? { ...item, readAt: null } : item
                    ),
                  },
                });
              },
              (e: HttpErrorResponse) => {
                patchState(this.state, {
                  errorMessage: extractErrorMessage(e),
                });
              }
            )
          )
      )
    )
  );

  readAllExpertEventNotifications = rxMethod<{
    request: ReadExpertEventNotificationRequest;
  }>(
    pipe(
      switchMap(payload =>
        this.#expertEventManagerNotificationService
          .readAllExpertEventNotifications(payload.request)
          .pipe(
            tapResponse(
              () => {
                const updated = this.state().slice?.content.map(item =>
                  item.readAt
                    ? item
                    : { ...item, readAt: new Date().toISOString() }
                );

                patchState(this.state, {
                  ...this.state(),
                  slice: {
                    ...this.state().slice,
                    content: updated,
                  },
                });
              },
              (e: HttpErrorResponse) => {
                patchState(this.state, {
                  errorMessage: extractErrorMessage(e),
                });
              }
            )
          )
      )
    )
  );

  activateExpertEventNotification = rxMethod<{ id: string }>(
    pipe(
      switchMap(payload =>
        this.#expertEventManagerNotificationService
          .activateExpertEventNotification(payload.id)
          .pipe(
            tapResponse(
              (id: string) => this.fetchExpertEventNotificationById({ id }),
              (e: HttpErrorResponse) => {
                patchState(this.state, {
                  errorMessage: extractErrorMessage(e),
                });
              }
            )
          )
      )
    )
  );

  inactivateExpertEventNotification = rxMethod<{ id: string }>(
    pipe(
      switchMap(payload =>
        this.#expertEventManagerNotificationService
          .inactivateExpertEventNotification(payload.id)
          .pipe(
            tapResponse(
              (id: string) => this.fetchExpertEventNotificationById({ id }),
              (e: HttpErrorResponse) => {
                patchState(this.state, {
                  errorMessage: extractErrorMessage(e),
                });
              }
            )
          )
      )
    )
  );
}
